document.documentElement.classList.remove('no-js');

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const motionEnabled = !document.body.classList.contains('theme-no-motion');
const phoneQuery = window.matchMedia('(max-width: 760px)');
const coarsePointer = window.matchMedia('(pointer: coarse)');
const phoneMotionEnabled = !document.body.classList.contains('mobile-no-motion');
const mobileMenuQuery = window.matchMedia('(max-width: 980px)');
let revealObserver;
const within = (scope, selector) => [
  ...(scope.matches && scope.matches(selector) ? [scope] : []),
  ...scope.querySelectorAll(selector)
];

window.requestAnimationFrame(() => document.body.classList.add('is-ready'));

function initReveal(scope = document) {
  if (!motionEnabled || reducedMotion.matches || !('IntersectionObserver' in window)) return;
  if (!revealObserver) {
    revealObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        revealObserver.unobserve(entry.target);
      });
    }, { threshold: 0.12 });
  }
  within(scope, '.section:not(.motion-ready)').forEach((section) => {
    section.classList.add('motion-ready');
    revealObserver.observe(section);
  });
  within(scope, '[data-reveal]:not(.reveal-ready)').forEach((element) => {
    if (element.dataset.reveal === 'none') return;
    element.classList.add('reveal-ready');
    revealObserver.observe(element);
  });
}

function initHeroMouse(scope = document) {
  if (!motionEnabled || reducedMotion.matches || coarsePointer.matches || phoneQuery.matches) return;
  within(scope, '[data-hero-interactive="true"]').forEach((hero) => {
    if (hero.dataset.motionBound === 'true') return;
    hero.dataset.motionBound = 'true';
    const strength = Number(hero.dataset.motionStrength || 12);
    const layers = hero.querySelectorAll('[data-depth]');
    hero.addEventListener('pointermove', (event) => {
      const rect = hero.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width - 0.5) * 2;
      const y = ((event.clientY - rect.top) / rect.height - 0.5) * 2;
      layers.forEach((layer) => {
        const depth = Number(layer.dataset.depth || 0.1);
        layer.style.translate = `${x * strength * depth}px ${y * strength * depth}px`;
      });
    });
    hero.addEventListener('pointerleave', () => layers.forEach((layer) => { layer.style.translate = '0 0'; }));
  });
}

let ticking = false;
function updateParallax() {
  document.querySelectorAll('.hero-custom').forEach((hero) => {
    const rect = hero.getBoundingClientRect();
    const heroProgress = Math.max(0, Math.min(1, -rect.top / Math.max(1, rect.height * 0.82)));
    hero.style.setProperty('--hero-scroll', heroProgress.toFixed(3));
    if (phoneQuery.matches || coarsePointer.matches) return;
    const progress = Math.max(-1, Math.min(1, (window.innerHeight / 2 - rect.top - rect.height / 2) / window.innerHeight));
    hero.querySelectorAll('[data-parallax]').forEach((item) => {
      const strength = Number(item.dataset.parallax || 0);
      item.style.translate = `0 ${progress * strength}px`;
    });
  });
  const max = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
  const pageProgress = document.querySelector('[data-page-progress]');
  if (pageProgress) pageProgress.style.transform = `scaleX(${Math.max(0, Math.min(1, window.scrollY / max))})`;
  document.querySelectorAll('[data-video-story]').forEach((story) => {
    const rect = story.getBoundingClientRect();
    const distance = Math.max(1, rect.height - window.innerHeight);
    const progress = Math.max(0, Math.min(1, -rect.top / distance));
    const bar = story.querySelector('[data-story-progress]');
    if (bar) bar.style.transform = `scaleY(${progress})`;
  });
  ticking = false;
}

function initMotion(scope = document) {
  initReveal(scope);
  initHeroMouse(scope);
  initComboCatalog(scope);
  initProductFinders(scope);
  initVideoStories(scope);
  initPopouts(scope);
  initProductWorlds(scope);
  initProductForms(scope);
  initPageTheatre(scope);
  updateParallax();
}

function initComboCatalog(scope = document) {
  within(scope, '[data-combo-catalog]').forEach((catalog) => {
    if (catalog.dataset.comboBound === 'true') return;
    catalog.dataset.comboBound = 'true';
    const boot = catalog.querySelector('[data-combo-boot]');
    const progress = catalog.querySelector('.combo-progress');
    const parallaxLayers = [...catalog.querySelectorAll('[data-combo-parallax]')];

    window.setTimeout(() => {
      if (boot) boot.classList.add('is-complete');
    }, reducedMotion.matches ? 0 : 1050);

    const updateCombo = () => {
      const rect = catalog.getBoundingClientRect();
      const distance = Math.max(1, catalog.offsetHeight - window.innerHeight);
      const amount = Math.max(0, Math.min(1, -rect.top / distance));
      catalog.style.setProperty('--combo-scroll', amount.toFixed(3));
      if (progress) progress.style.transform = `scaleX(${amount})`;
      if (!reducedMotion.matches && !phoneQuery.matches) {
        parallaxLayers.forEach((layer) => {
          const strength = Number(layer.dataset.comboParallax || 0);
          layer.style.transform = `translate3d(0,${amount * strength}px,0)`;
        });
      }
    };

    if (!reducedMotion.matches && !coarsePointer.matches) {
      catalog.addEventListener('pointermove', (event) => {
        const rect = catalog.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / Math.max(1, rect.width) - .5) * 70;
        const y = Math.max(-35, Math.min(35, (event.clientY - rect.top) / Math.max(1, rect.height) * 70 - 35));
        catalog.style.setProperty('--combo-x', `${x}px`);
        catalog.style.setProperty('--combo-y', `${y}px`);
      });
    }

    window.addEventListener('scroll', updateCombo, { passive: true });
    updateCombo();
  });
}

function initProductFinders(scope = document) {
  within(scope, '[data-product-finder]').forEach((finder) => {
    if (finder.dataset.bound === 'true') return;
    finder.dataset.bound = 'true';
    const groups = [...finder.querySelectorAll('[data-finder-group]')].filter((group) => group.querySelector('[data-choice]'));
    const progress = finder.querySelector('[data-finder-progress]');
    const status = finder.querySelector('[data-finder-status]');
    const refresh = () => {
      const complete = groups.filter((group) => group.querySelector('.is-selected')).length;
      if (progress) progress.style.width = `${groups.length ? complete / groups.length * 100 : 0}%`;
    };
    finder.addEventListener('click', (event) => {
      const choice = event.target.closest('[data-choice]');
      if (choice) {
        finder.querySelectorAll(`[data-group="${choice.dataset.group}"]`).forEach((item) => item.classList.remove('is-selected'));
        choice.classList.add('is-selected');
        refresh();
        const video = finder.querySelector('video');
        if (video) { video.currentTime = 0; video.play().catch(() => {}); }
        return;
      }
      if (!event.target.closest('[data-finder-submit]')) return;
      const selected = [...finder.querySelectorAll('.finder-choice.is-selected')];
      if (!selected.length) { if (status) status.textContent = 'Choose at least one option first.'; return; }
      let destination = selected.find((item) => item.dataset.url)?.dataset.url || '/search?type=product&q=*';
      const parameters = new URLSearchParams();
      selected.forEach((item) => {
        if (item.dataset.tag) parameters.append('filter.p.tag', item.dataset.tag);
        if (item.dataset.max) parameters.set('filter.v.price.lte', item.dataset.max);
      });
      if (status) status.textContent = 'Abu Stuff found something—here we go!';
      window.setTimeout(() => { window.location.href = destination + (parameters.toString() ? `?${parameters}` : ''); }, 700);
    });
    refresh();
  });
}

function initVideoStories(scope = document) {
  if (!('IntersectionObserver' in window)) return;
  within(scope, '[data-video-story]').forEach((story) => {
    if (story.dataset.bound === 'true') return;
    story.dataset.bound = 'true';
    const steps = [...story.querySelectorAll('[data-story-step]')];
    const visuals = [...story.querySelectorAll('[data-story-visual]')];
    const playButton = story.querySelector('[data-story-play]');
    let storyVisible = false;
    let activeIndex = 0;
    const prepareVideo = (video) => {
      if (!video) return;
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.setAttribute('autoplay', '');
      if (video.dataset.mobilePrepared !== 'true') {
        video.dataset.mobilePrepared = 'true';
        const visual = video.closest('[data-story-visual]');
        video.addEventListener('playing', () => { if (visual) visual.classList.add('has-video'); story.classList.remove('needs-play'); });
        video.addEventListener('error', () => { if (visual) visual.classList.remove('has-video'); story.classList.add('video-fallback-active'); });
      }
      if (video.readyState === 0) video.load();
    };
    const playActiveVideo = () => {
      const video = visuals[activeIndex]?.querySelector('video');
      if (!video || reducedMotion.matches || (phoneQuery.matches && !phoneMotionEnabled)) return;
      prepareVideo(video);
      const result = video.play();
      if (result && typeof result.then === 'function') {
        result.then(() => story.classList.remove('needs-play')).catch(() => story.classList.add('needs-play'));
      }
    };
    const activate = (index) => {
      activeIndex = index;
      steps.forEach((step, i) => step.classList.toggle('is-active', i === index));
      visuals.forEach((visual, i) => {
        visual.classList.toggle('is-active', i === index);
        const video = visual.querySelector('video');
        if (!video) return;
        prepareVideo(video);
        if (i !== index) video.pause();
      });
      if (storyVisible) playActiveVideo();
      const active = visuals[index];
      if (active) {
        story.style.setProperty('--active-bg', active.dataset.sceneBackground || 'var(--story-bg)');
        story.style.setProperty('--active-accent', active.dataset.sceneAccent || 'var(--story-accent)');
      }
    };
    const observer = new IntersectionObserver((entries) => {
      const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (visible) activate(Number(visible.target.dataset.storyStep));
    }, { threshold: [0.35, 0.55, 0.75], rootMargin: '-15% 0px -20% 0px' });
    steps.forEach((step) => observer.observe(step));
    activate(0);
    const visibilityObserver = new IntersectionObserver((entries) => {
      storyVisible = Boolean(entries[0] && entries[0].isIntersecting);
      if (storyVisible) playActiveVideo();
      else visuals.forEach((visual) => { const video = visual.querySelector('video'); if (video) video.pause(); });
    }, { threshold: 0.02 });
    visibilityObserver.observe(story);
    if (playButton) playButton.addEventListener('click', () => playActiveVideo());
    const mobileVideos = [...story.querySelectorAll('[data-mobile-story-media] video')];
    mobileVideos.forEach(prepareVideo);
    const mobileVideoObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target.querySelector('video');
        if (!video || !phoneQuery.matches) return;
        if (entry.isIntersecting && !reducedMotion.matches && phoneMotionEnabled) video.play().catch(() => {});
        else video.pause();
      });
    }, { threshold: 0.25, rootMargin: '10% 0px 10% 0px' });
    story.querySelectorAll('[data-mobile-story-media]').forEach((media) => mobileVideoObserver.observe(media));
  });
}

function initPageTheatre(scope = document) {
  if (scope === document && !document.querySelector('[data-page-progress]')) {
    const progress = document.createElement('span');
    progress.className = 'page-progress';
    progress.dataset.pageProgress = 'true';
    progress.setAttribute('aria-hidden', 'true');
    document.body.appendChild(progress);
  }
  if (scope === document && !coarsePointer.matches && !reducedMotion.matches && !document.querySelector('[data-cursor-aura]')) {
    const aura = document.createElement('span');
    aura.className = 'cursor-aura';
    aura.dataset.cursorAura = 'true';
    aura.setAttribute('aria-hidden', 'true');
    document.body.appendChild(aura);
    window.addEventListener('pointermove', (event) => {
      aura.style.transform = `translate3d(${event.clientX}px,${event.clientY}px,0)`;
    }, { passive: true });
    document.addEventListener('pointerover', (event) => aura.classList.toggle('is-over', Boolean(event.target.closest('a,button,.product-card,.category-card'))));
  }
  within(scope, '.button:not([data-theatre-bound])').forEach((button) => {
    button.dataset.theatreBound = 'true';
    if (coarsePointer.matches || reducedMotion.matches) return;
    button.addEventListener('pointermove', (event) => {
      const rect = button.getBoundingClientRect();
      button.style.translate = `${(event.clientX - rect.left - rect.width / 2) * .09}px ${(event.clientY - rect.top - rect.height / 2) * .12}px`;
    });
    button.addEventListener('pointerleave', () => { button.style.translate = '0 0'; });
  });
}

let reactionIndex = 0;
function showAbuPopout(popout) {
  const clips = [...popout.querySelectorAll('[data-popout-clip]')];
  if (!clips.length || reducedMotion.matches || (phoneQuery.matches && !phoneMotionEnabled)) return;
  const index = reactionIndex++ % clips.length;
  clips.forEach((clip, i) => {
    clip.classList.toggle('is-active', i === index);
    const video = clip.querySelector('video');
    if (!video) return;
    video.pause();
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    if (i === index) { video.currentTime = 0; if (video.readyState === 0) video.load(); video.play().catch(() => {}); }
  });
  const message = popout.querySelector('[data-popout-message]');
  if (message) message.textContent = clips[index].dataset.message || '';
  popout.classList.add('is-active');
  window.clearTimeout(popout._hideTimer);
  popout._hideTimer = window.setTimeout(() => popout.classList.remove('is-active'), Number(popout.dataset.duration || 7) * 1000);
}

function initPopouts(scope = document) {
  within(scope, '[data-abu-popout]').forEach((popout) => {
    if (popout.dataset.bound === 'true') return;
    popout.dataset.bound = 'true';
    const close = popout.querySelector('.abu-popout__close');
    if (close) close.addEventListener('click', () => popout.classList.remove('is-active'));
    let lastScrollAppearance = -100;
    const onScroll = () => {
      const max = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
      const percentage = window.scrollY / max * 100;
      const firstTrigger = Number(popout.dataset.scrollTrigger || 25);
      const interval = Number(popout.dataset.scrollInterval || 25);
      const repeat = phoneQuery.matches ? popout.dataset.mobileScrollRepeat === 'true' : popout.dataset.scrollRepeat === 'true';
      if (percentage >= firstTrigger && (lastScrollAppearance < 0 || (repeat && percentage - lastScrollAppearance >= interval))) {
        lastScrollAppearance = percentage;
        showAbuPopout(popout);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
  });
}

function initProductWorlds(scope = document) {
  within(scope, '[data-product-world]').forEach((world) => {
    if (world.dataset.worldBound === 'true') return;
    world.dataset.worldBound = 'true';
    const slides = [...world.querySelectorAll('[data-product-slide]')];
    const thumbs = [...world.querySelectorAll('[data-product-thumb]')];
    const select = world.querySelector('[data-variant-select]');
    const price = world.querySelector('[data-product-price]');
    const host = world.querySelector('[data-abu-product-host]');
    const counter = world.querySelector('[data-gallery-counter]');
    const previous = world.querySelector('[data-gallery-prev]');
    const next = world.querySelector('[data-gallery-next]');
    const stage = world.querySelector('.product-gallery__stage');
    let activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));

    const activateIndex = (requestedIndex, scrollThumb = true) => {
      if (!slides.length) return;
      activeIndex = (requestedIndex + slides.length) % slides.length;
      const mediaId = slides[activeIndex].dataset.mediaId;
      slides.forEach((slide, index) => {
        const active = index === activeIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
      thumbs.forEach((thumb) => {
        const active = thumb.dataset.mediaId === String(mediaId);
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-pressed', active ? 'true' : 'false');
        if (active && scrollThumb) thumb.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
      });
      if (counter) counter.textContent = `${activeIndex + 1} / ${slides.length}`;
    };

    const activateMedia = (mediaId) => {
      if (!mediaId) return;
      const index = slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (index >= 0) activateIndex(index);
    };

    const celebrate = () => {
      if (!host || reducedMotion.matches || (phoneQuery.matches && !phoneMotionEnabled)) return;
      host.classList.remove('is-celebrating');
      void host.offsetWidth;
      host.classList.add('is-celebrating');
      const video = host.querySelector('video');
      if (video) { video.currentTime = 0; video.play().catch(() => {}); }
      for (let i = 0; i < 7; i += 1) {
        const spark = document.createElement('i');
        spark.className = 'abu-product-spark';
        spark.style.setProperty('--spark-angle', `${i * 51}deg`);
        spark.style.setProperty('--spark-delay', `${i * 35}ms`);
        host.appendChild(spark);
        window.setTimeout(() => spark.remove(), 900);
      }
    };

    thumbs.forEach((thumb) => thumb.addEventListener('click', () => {
      activateMedia(thumb.dataset.mediaId);
      celebrate();
    }));

    if (previous) previous.addEventListener('click', () => { activateIndex(activeIndex - 1); celebrate(); });
    if (next) next.addEventListener('click', () => { activateIndex(activeIndex + 1); celebrate(); });

    if (stage && slides.length > 1) {
      let touchStartX = 0;
      stage.addEventListener('touchstart', (event) => { touchStartX = event.changedTouches[0]?.clientX || 0; }, { passive: true });
      stage.addEventListener('touchend', (event) => {
        const endX = event.changedTouches[0]?.clientX || 0;
        const distance = endX - touchStartX;
        if (Math.abs(distance) < 42) return;
        activateIndex(activeIndex + (distance < 0 ? 1 : -1));
      }, { passive: true });
    }

    if (select) select.addEventListener('change', () => {
      const option = select.options[select.selectedIndex];
      if (price && option.dataset.price) price.textContent = option.dataset.price;
      activateMedia(option.dataset.mediaId);
      celebrate();
      document.querySelectorAll('[data-abu-popout]').forEach((popout) => showAbuPopout(popout));
    });

    activateIndex(activeIndex, false);


    const gallery = world.querySelector('[data-product-gallery]');
    if (gallery && motionEnabled && !reducedMotion.matches && !coarsePointer.matches) {
      gallery.addEventListener('pointermove', (event) => {
        const rect = gallery.getBoundingClientRect();
        const x = (event.clientX - rect.left) / rect.width - .5;
        const y = (event.clientY - rect.top) / rect.height - .5;
        gallery.style.setProperty('--gallery-rotate-x', `${y * -2.2}deg`);
        gallery.style.setProperty('--gallery-rotate-y', `${x * 2.2}deg`);
      });
      gallery.addEventListener('pointerleave', () => {
        gallery.style.setProperty('--gallery-rotate-x', '0deg');
        gallery.style.setProperty('--gallery-rotate-y', '0deg');
      });
    }
  });
}

function initProductForms(scope = document) {
  within(scope, '[data-product-form]').forEach((form) => {
    if (form.dataset.bound === 'true') return;
    form.dataset.bound = 'true';
    form.querySelectorAll('[data-quantity-control]').forEach((control) => {
      const input = control.querySelector('input[name="quantity"]');
      const minus = control.querySelector('[data-quantity-minus]');
      const plus = control.querySelector('[data-quantity-plus]');
      const change = (amount) => {
        if (!input) return;
        const minimum = Number(input.min || 1);
        input.value = String(Math.max(minimum, Number(input.value || minimum) + amount));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      };
      if (minus) minus.addEventListener('click', () => change(-1));
      if (plus) plus.addEventListener('click', () => change(1));
    });
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const status = form.querySelector('[data-product-status]');
      const button = form.querySelector('[type="submit"]');
      const originalLabel = button ? button.textContent : '';
      if (status) status.textContent = 'Adding your stuff…';
      if (button) { button.disabled = true; button.classList.add('is-loading'); }
      try {
        const response = await fetch('/cart/add.js', { method: 'POST', body: new FormData(form), headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Could not add this product.');
        if (status) status.textContent = 'Added! Abu Stuff approves.';
        if (button) { button.textContent = 'ADDED ✓'; button.classList.add('is-added'); }
        try {
          const cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
          if (cartResponse.ok) {
            const cart = await cartResponse.json();
            document.querySelectorAll('[data-cart-count]').forEach((count) => { count.textContent = cart.item_count; });
            document.querySelectorAll('[data-header-cart]').forEach((cartLink) => {
              cartLink.setAttribute('aria-label', `Cart with ${cart.item_count} items`);
              cartLink.classList.remove('is-updated');
              void cartLink.offsetWidth;
              cartLink.classList.add('is-updated');
            });
          }
        } catch (_) {
          // The product is already in the cart; the count can refresh on the next page.
        }
      } catch (error) {
        if (status) status.textContent = error.message;
      } finally {
        window.setTimeout(() => {
          if (!button) return;
          button.disabled = false;
          button.textContent = originalLabel;
          button.classList.remove('is-loading', 'is-added');
        }, 1100);
      }
    });
  });
}

let mobileNavTimer;
function setMobileNavigation(opening, returnFocus = false) {
  const nav = document.querySelector('[data-mobile-nav]');
  const toggle = document.querySelector('[data-mobile-toggle]');
  if (!nav || !toggle) return;
  window.clearTimeout(mobileNavTimer);
  toggle.setAttribute('aria-expanded', String(opening));
  toggle.setAttribute('aria-label', opening ? 'Close menu' : 'Open menu');
  document.body.classList.toggle('mobile-menu-open', opening);
  if (opening) {
    nav.removeAttribute('hidden');
    window.requestAnimationFrame(() => nav.classList.add('is-open'));
  } else {
    nav.classList.remove('is-open');
    mobileNavTimer = window.setTimeout(() => nav.setAttribute('hidden', ''), reducedMotion.matches ? 0 : 280);
    if (returnFocus) toggle.focus();
  }
}

window.addEventListener('scroll', () => {
  if (!motionEnabled || reducedMotion.matches || ticking) return;
  ticking = true;
  window.requestAnimationFrame(updateParallax);
}, { passive: true });

document.addEventListener('click', (event) => {
  if (motionEnabled && !reducedMotion.matches && !coarsePointer.matches && event.detail !== 0 && event.target.closest('a,button')) {
    const burst = document.createElement('span');
    burst.className = 'button-burst';
    burst.style.left = `${event.clientX}px`;
    burst.style.top = `${event.clientY}px`;
    document.body.appendChild(burst);
    window.setTimeout(() => burst.remove(), 700);
  }
  const toggle = event.target.closest('[data-mobile-toggle]');
  if (toggle) {
    setMobileNavigation(toggle.getAttribute('aria-expanded') !== 'true');
  }
  const mobileLink = event.target.closest('[data-mobile-nav] a');
  if (mobileLink) {
    setMobileNavigation(false);
  }
  if (event.target.closest('.button, button[type="submit"], [data-abu-trigger]')) {
    document.querySelectorAll('[data-abu-popout][data-click-trigger="true"]').forEach((popout) => {
      if (!phoneQuery.matches || popout.dataset.mobileClickTrigger === 'true') showAbuPopout(popout);
    });
  }
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  const nav = document.querySelector('[data-mobile-nav]');
  const menuButton = document.querySelector('[data-mobile-toggle]');
  if (!nav || nav.hasAttribute('hidden')) return;
  setMobileNavigation(false, true);
});

mobileMenuQuery.addEventListener('change', (event) => {
  if (!event.matches) setMobileNavigation(false);
});

document.addEventListener('shopify:section:load', (event) => initMotion(event.target));
initMotion();
if (reducedMotion.matches || (phoneQuery.matches && !phoneMotionEnabled)) document.querySelectorAll('video[autoplay]').forEach((video) => video.pause());
